/** Shared network contract for parent and child surfaces.
 *
 * Server detail strings are deliberately kept out of UI copy. They are useful
 * to logs and tests, but surfaces render the stable error code instead.
 */
export type UiErrorCode =
  | 'aborted'
  | 'offline'
  | 'unauthorized'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'timeout'
  | 'service_unavailable'
  | 'unknown';

export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
  readonly code: UiErrorCode;
  readonly status: number | null;
  readonly retryable: boolean;
  readonly requestId: string | null;

  constructor(
    code: UiErrorCode,
    message = 'The request could not be completed.',
    status: number | null = null,
    requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.retryable = code === 'offline' || code === 'timeout' || code === 'service_unavailable' || code === 'rate_limited';
    this.requestId = requestId;
  }
}

export function isAbortError(error: unknown): boolean {
  return (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && error.name === 'AbortError');
}

export function isTimeoutError(error: unknown): boolean {
  return (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'TimeoutError')
    || (error instanceof Error && error.name === 'TimeoutError');
}

export function toApiError(error: unknown, fallback = 'The request could not be completed.'): ApiError {
  if (error instanceof ApiError) return error;
  if (isAbortError(error)) {
    return new ApiError('aborted');
  }
  if (isTimeoutError(error)) {
    return new ApiError('timeout');
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return new ApiError('offline');
  }
  return new ApiError('unknown', fallback);
}

function errorCodeForStatus(status: number): UiErrorCode {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 409 || status === 422) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'service_unavailable';
  return 'unknown';
}

export async function requestJson<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS
): Promise<T> {
  let response: Response;
  const requestController = new AbortController();
  let timedOut = false;
  let callerAborted = init.signal?.aborted || false;
  const forwardCallerAbort = () => {
    callerAborted = true;
    requestController.abort();
  };
  init.signal?.addEventListener('abort', forwardCallerAbort, { once: true });
  if (callerAborted) requestController.abort();
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    requestController.abort();
  }, timeoutMs);
  try {
    response = await fetch(input, { ...init, signal: requestController.signal });
  } catch (error) {
    if (timedOut) throw new ApiError('timeout');
    if (callerAborted || isAbortError(error)) throw error;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiError('offline');
    }
    throw new ApiError('service_unavailable');
  } finally {
    window.clearTimeout(timeoutId);
    init.signal?.removeEventListener('abort', forwardCallerAbort);
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const detail = typeof payload?.detail === 'string' ? payload.detail : undefined;
    throw new ApiError(
      errorCodeForStatus(response.status),
      detail || undefined,
      response.status,
      response.headers.get('x-request-id'),
    );
  }
  return response.json() as Promise<T>;
}

export function authHeaders(json = false): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}
